import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { devFilesApi, databasesApi } from '../../services/api';
import { activeDbName } from '../../services/activeDatabase';
import { useNotifications } from '../../context/NotificationContext';
import { RefreshCw, FilterX } from 'lucide-react';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import StuiSelect from '../../components/common/StuiSelect';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import NoMatchState from '../../components/common/NoMatchState';
import { useListTrunk } from '../../hooks/useListTrunk';
import { hasColumnFilters, SEARCH_HINT } from '../../utils/listSearch';
import { formatDateTime } from '../../utils/dateTime';

/**
 * Schema Viewer - columns, keys and foreign keys read live from INFORMATION_SCHEMA.
 *
 * The database picker chooses what this page reads and nothing else: the request carries
 * ?database=, and this tab's database (the X-Database-Id every other call sends) is never
 * changed. The admin database is an option too, for
 * AppSec_* / AppCfg_* / DevTrack_*.
 */
const ADMIN_DATABASE = '__admin__';

const SEARCH_FIELD_LABELS = [
  { key: 'table', label: 'Table' }, { key: 'column', label: 'Column' }, { key: 'dataType', label: 'Data Type' },
  { key: 'nullable', label: 'Nullable' }, { key: 'foreignKey', label: 'Foreign Key' },
];
const SEARCH_FIELDS = SEARCH_FIELD_LABELS.map(f => f.key);

const COLUMNS = [
  { key: 'table', label: 'Table' },
  { key: 'column', label: 'Column' },
  { key: 'dataType', label: 'Data Type' },
  { key: 'nullable', label: 'Nullable' },
  { key: 'isPrimaryKey', label: 'PK' },
  { key: 'isIdentity', label: 'Identity' },
  { key: 'foreignKey', label: 'Foreign Key' },
];

const SchemaViewerPage = () => {
  const { notify } = useNotifications();
  const [databases, setDatabases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState(null);

  // Start on the database the user is working in, when it is in the list.
  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => {
        const list = res.data || [];
        setDatabases(list);
        const current = activeDbName();
        setSelected(list.some(d => d.databaseName === current) ? current : (list[0]?.databaseName ?? ADMIN_DATABASE));
      })
      .catch(() => { setDatabases([]); setSelected(ADMIN_DATABASE); });
  }, []);

  const loadSchema = useCallback(async () => {
    if (!selected) return;
    try {
      setLoading(true);
      const response = await devFilesApi.getLiveSchema(selected);
      setSchema(response.data);
    } catch (err) {
      setSchema(null);
      notify.error(err.response?.data?.message || 'Failed to load schema');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => { loadSchema(); }, [loadSchema]);

  // One row per column. PK / Identity are text so the column filter can list and match them.
  const rows = useMemo(() => {
    if (!schema?.tables) return [];
    return schema.tables.flatMap(table => table.columns.map(col => ({
      id: `${table.name}.${col.name}`,
      table: table.name,
      column: col.name,
      dataType: col.dataType,
      nullable: col.isNullable ? 'NULL' : 'NOT NULL',
      isPrimaryKey: col.isPrimaryKey ? 'Yes' : '',
      isIdentity: col.isIdentity ? 'Yes' : '',
      foreignKey: col.foreignKeyTable || '',
    })));
  }, [schema]);

  const { searched, filtered } = useListTrunk({
    rows, query: searchTerm, searchFields: SEARCH_FIELDS, labels: SEARCH_FIELD_LABELS,
    filters, sortConfig, columns: COLUMNS,
  });

  const hasFilters = hasColumnFilters(filters);
  const tableCount = schema?.tables?.length || 0;
  const pkCount = rows.filter(r => r.isPrimaryKey).length;
  const fkCount = rows.filter(r => r.foreignKey).length;
  const queried = schema?.lastQueried ? formatDateTime(schema.lastQueried, { mode: 'time', valueZone: 'utc' }) : null;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <h1>Schema Viewer</h1>
          {schema && (
            <span className="stui-module-header-meta">
              {schema.databaseName} &middot; {tableCount} tables &middot; {rows.length} columns &middot; {pkCount} PKs &middot; {fkCount} FKs{queried ? ` · queried ${queried}` : ''}
            </span>
          )}
        </div>
        <div className="stui-module-header-actions">
          <StuiSelect className="stui-select" value={selected ?? ''} onChange={e => setSelected(e.target.value)} title="Database to read" aria-label="Database">
            {databases.map(d => <option key={d.databaseName} value={d.databaseName}>{d.displayName || d.databaseName}</option>)}
            <option value={ADMIN_DATABASE}>Admin database</option>
          </StuiSelect>
          {hasFilters && (
            <button onClick={() => setFilters({})} className="stui-clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{filtered.length}/{rows.length}</span>
            </button>
          )}
          <div className="stui-search-wrapper">
            <SearchClearIcon size={14} searchTerm={searchTerm} onClear={() => setSearchTerm('')} />
            <input
              className="stui-search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search columns..."
              title={SEARCH_HINT}
            />
          </div>
          <button className="stui-crud-btn" onClick={loadSchema} disabled={loading || !selected} title="Refresh">
            <RefreshCw size={18} className={loading ? 'stui-spinning' : ''} />
          </button>
        </div>
      </div>

      {loading ? (
        <span className="stui-loading">Loading schema...</span>
      ) : rows.length > 0 && filtered.length === 0 ? (
        <NoMatchState hasSearch={!!searchTerm} hasFilters={hasFilters} onClearSearch={() => setSearchTerm('')} onClearFilters={() => setFilters({})} />
      ) : (
        <div className="stui-datasheet-view">
          <FilterableDataGrid
            data={filtered}
            allData={searched}
            sortConfig={sortConfig || { key: null, direction: null }}
            onSortChange={setSortConfig}
            columns={COLUMNS}
            filters={filters}
            setFilters={setFilters}
          />
        </div>
      )}
    </div>
  );
};

export default SchemaViewerPage;
